import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';

const H2 = ({ children }) => (
  <h2 className="font-['Manrope'] text-lg font-bold text-[#1A1A1A] mb-3">{children}</h2>
);
const Section = ({ children }) => (
  <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6">{children}</section>
);
const P = ({ children }) => <p className="text-[#374151] leading-relaxed mb-3 last:mb-0">{children}</p>;

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="privacy-page">
      <a href="#main-content" className="skip-to-content">Skip to main content</a>

      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/lifelink-logo.png" alt="LIFE Link" className="h-10 w-auto object-contain" />
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#34657f] hover:underline rounded px-2 py-1.5"
            data-testid="privacy-back-link"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Back to sign in
          </Link>
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className="max-w-3xl mx-auto px-6 py-10 outline-none">
        <div className="flex items-center gap-3 mb-2">
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#34657f] text-white">
            <ShieldCheck className="w-6 h-6" aria-hidden="true" />
          </span>
          <h1 className="font-['Manrope'] text-3xl sm:text-4xl font-bold text-[#1A1A1A]">Privacy Policy</h1>
        </div>
        <p className="text-sm text-[#5C5C5C] mb-8">Effective date: June 2026</p>

        <Section>
          <H2>1. Who we are</H2>
          <P>
            LIFE Link ("the Portal," "we," "us") is the online portal for the Project LIFE program,
            operated by Butler Tech in partnership with Aspiritech. The Portal provides curriculum,
            training resources, and a professional community for authorized transition professionals
            ("users"). This Privacy Policy explains what information we collect, how we use it, and the
            choices you have.
          </P>
        </Section>

        <Section>
          <H2>2. Information we collect</H2>
          <ul className="list-disc pl-5 space-y-2 text-[#374151] leading-relaxed mb-3 last:mb-0">
            <li><strong>Account information</strong>: your name, email address, portal role, and — if provided — job title and school district. Accounts are created by program administrators; we do not accept self-registration.</li>
            <li><strong>Content you submit</strong>: forum posts, comments, reactions, success stories, and any photos, videos, or documents you upload.</li>
            <li><strong>Usage information</strong>: sign-in times, document views and downloads, and notification preferences, used to operate the Portal and provide administrators with aggregate usage insights.</li>
            <li><strong>Google Drive connection (optional)</strong>: if you choose to connect your Google account to copy resources to your own Drive, we store an access token limited to the narrow <code>drive.file</code> scope, your Google account email, and your most recently chosen destination folder. We can only access files and folders the Portal itself creates in your Drive — never your other Drive content.</li>
            <li><strong>Cookies and local storage</strong>: we use browser storage strictly to keep you signed in and remember interface preferences. We do not use advertising or cross-site tracking cookies.</li>
          </ul>
        </Section>

        <Section>
          <H2>3. How we use information</H2>
          <ul className="list-disc pl-5 space-y-2 text-[#374151] leading-relaxed mb-3 last:mb-0">
            <li>To provide, maintain, and secure the Portal and its features</li>
            <li>To send service emails you or your administrator initiate (such as the welcome email) and optional notifications you can control in your profile settings</li>
            <li>To operate community features such as the forum and success stories</li>
            <li>To give program administrators aggregate insights (for example, download counts) that help improve the program</li>
          </ul>
          <P>We do not sell, rent, or trade personal information, and we do not use it for advertising.</P>
        </Section>

        <Section>
          <H2>4. Student information — FERPA and COPPA</H2>
          <P>
            The Portal is designed for adult education professionals, not for students. We do not knowingly
            collect personal information from children under 13. Users are responsible for ensuring that any
            student information, photos, or stories they share on the Portal comply with the Family
            Educational Rights and Privacy Act (FERPA), IDEA, their district's policies, and any required
            parental/guardian consents. If you believe student information has been shared without proper
            consent, contact us immediately at{' '}
            <a href="mailto:projectlife@butlertech.org" className="text-[#34657f] font-bold underline">projectlife@butlertech.org</a>{' '}
            and we will promptly remove it.
          </P>
        </Section>

        <Section>
          <H2>5. How information is shared</H2>
          <ul className="list-disc pl-5 space-y-2 text-[#374151] leading-relaxed mb-3 last:mb-0">
            <li><strong>Within the Portal community</strong>: your name, role, and content you post are visible to other authorized users.</li>
            <li><strong>Service providers</strong>: we use trusted providers to operate the Portal — including hosting infrastructure, email delivery (Resend), and Google APIs (Drive integration). Providers receive only the information needed to perform their service.</li>
            <li><strong>Legal requirements</strong>: we may disclose information if required by law or to protect the safety, rights, or property of users or the program.</li>
          </ul>
          <P>
            Our use of information received from Google APIs adheres to the{' '}
            <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="text-[#1f7fa6] underline font-medium">
              Google API Services User Data Policy
            </a>, including the Limited Use requirements.
          </P>
        </Section>

        <Section>
          <H2>6. Data security</H2>
          <P>
            We use industry-standard safeguards: all traffic is encrypted in transit (HTTPS), passwords are
            stored only as salted hashes, access is role-based, and Google tokens are held with the minimum
            possible scope. No method of transmission or storage is 100% secure, but we work continuously to
            protect your information.
          </P>
        </Section>

        <Section>
          <H2>7. Data retention and your choices</H2>
          <ul className="list-disc pl-5 space-y-2 text-[#374151] leading-relaxed mb-3 last:mb-0">
            <li>Account data is retained while your account is active and removed when your account is deleted by an administrator.</li>
            <li>You can manage notification preferences on your profile page at any time.</li>
            <li>You can disconnect your Google Drive at any time from your profile page, which stops all Portal access to your Drive.</li>
            <li>You may request access to, correction of, or deletion of your personal information by emailing{' '}
              <a href="mailto:projectlife@butlertech.org" className="text-[#34657f] font-bold underline">projectlife@butlertech.org</a>.</li>
          </ul>
        </Section>

        <Section>
          <H2>8. Changes to this policy</H2>
          <P>
            We may update this Privacy Policy from time to time. Material changes will be noted by updating
            the effective date above, and continued use of the Portal after changes constitutes acceptance.
          </P>
        </Section>

        <Section>
          <H2>9. Contact us</H2>
          <P>
            Questions about privacy? Contact the Project LIFE National Team at{' '}
            <a href="mailto:projectlife@butlertech.org" className="text-[#34657f] font-bold underline" data-testid="privacy-contact-link">
              projectlife@butlertech.org
            </a>. For accessibility questions, see our{' '}
            <Link to="/accessibility" className="text-[#1f7fa6] underline font-medium">Accessibility Statement</Link>.
          </P>
        </Section>

        <p className="text-sm text-[#5C5C5C] text-center pb-6">
          &copy; 2026 LIFE Link | Project LIFE | Butler Tech. All rights reserved. ·{' '}
          <Link to="/terms" className="text-[#1a6f92] underline">Terms &amp; Conditions</Link>
        </p>
      </main>
    </div>
  );
}
